import React, { useState, useEffect } from 'react';
import { Badge, Tooltip, Button } from 'antd';
import { WifiOutlined, DisconnectOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../services/api';
import simpleErrorHandler from '../utils/simpleErrorHandler';

interface NetworkStatusProps {
  showDetails?: boolean;
}

const NetworkStatus: React.FC<NetworkStatusProps> = ({ showDetails = false }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [errorCount, setErrorCount] = useState(0);

  const checkConnection = async () => {
    setIsChecking(true);
    try {
      await api.get('/health');
      setIsOnline(true);
      setLastCheck(new Date());
      setErrorCount(0);
    } catch (error) {
      setIsOnline(false);
      setErrorCount(prev => prev + 1);
      simpleErrorHandler.logError(error, 'NetworkStatus');
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    // 监听网络状态变化
    const handleOnline = () => {
      setIsOnline(true);
      checkConnection();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // 定期检查连接状态
    const interval = setInterval(checkConnection, 60000); // 每分钟检查一次

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  const getStatusColor = () => {
    if (isChecking) return 'processing';
    if (isOnline) return 'success';
    if (errorCount > 3) return 'error';
    return 'warning';
  };

  const getStatusText = () => {
    if (isChecking) return '检查中...';
    if (isOnline) return '已连接';
    if (errorCount > 3) return '连接失败';
    return '连接不稳定';
  };

  const getStatusIcon = () => {
    if (isChecking) return <ReloadOutlined spin />;
    if (isOnline) return <WifiOutlined />;
    return <DisconnectOutlined />;
  };

  if (showDetails) {
    return (
      <div style={{ padding: '8px', border: '1px solid #d9d9d9', borderRadius: '6px', backgroundColor: '#fafafa' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <Badge status={getStatusColor()} />
          <span>{getStatusText()}</span>
          <Button 
            size="small" 
            icon={<ReloadOutlined />} 
            onClick={checkConnection}
            loading={isChecking}
          >
            检查
          </Button>
        </div>
        {lastCheck && (
          <div style={{ fontSize: '12px', color: '#666' }}>
            最后检查: {lastCheck.toLocaleTimeString()}
          </div>
        )}
        {errorCount > 0 && (
          <div style={{ fontSize: '12px', color: '#ff4d4f' }}>
            错误次数: {errorCount}
          </div>
        )}
      </div>
    );
  }

  return (
    <Tooltip title={`网络状态: ${getStatusText()}`}>
      <div 
        style={{ 
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative'
        }}
        onClick={checkConnection}
      >
        {getStatusIcon()}
        <div 
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: getStatusColor() === 'success' ? '#52c41a' : 
                           getStatusColor() === 'error' ? '#ff4d4f' : '#faad14',
            marginTop: '2px',
            boxShadow: '0 0 2px rgba(0,0,0,0.2)'
          }}
        />
      </div>
    </Tooltip>
  );
};

export default NetworkStatus;

